import { useEffect, useState } from 'react';
import { useAuthStore } from '@/store';
import { chatsApi } from '@/lib/api/chats';

const TTL = 5 * 60 * 1000;
const cache = new Map();
const pending = new Map();
const cacheKey = (identity, mode, candidate) => JSON.stringify([identity, mode, candidate.conversationId, candidate.updatedAt]);
function cached(key) {
  const entry = cache.get(key);
  if (entry && entry.expires > Date.now()) return entry;
  cache.delete(key);
  return null;
}
function remember(key, suggestion) {
  if (cache.size >= 80) cache.delete(cache.keys().next().value);
  cache.set(key, { suggestion, expires: Date.now() + (suggestion ? TTL : 30000) });
}
function fetchSuggestion(key, candidate, mode) {
  if (!pending.has(key)) pending.set(key, chatsApi.fetchDiscoveryContinuation(candidate.conversationId, mode)
    .then(({ suggestion }) => {
      const valid = suggestion?.conversationId === candidate.conversationId && typeof suggestion.prompt === 'string'
        && suggestion.prompt.trim() && Number.isFinite(Date.parse(suggestion.updatedAt))
        && Date.parse(suggestion.updatedAt) >= Date.parse(candidate.updatedAt);
      remember(key, valid ? suggestion : null);
      return valid ? suggestion : null;
    }).finally(() => pending.delete(key)));
  return pending.get(key);
}
export default function useDiscoveryContinuations(candidates, mode, enabled) {
  const userId = useAuthStore(s => s.user?.id);
  const session = useAuthStore(s => s._sessionGeneration);
  const identity = `${userId}:${session}`;
  const key = JSON.stringify([identity, mode, enabled, candidates.map(item => [item.conversationId, item.updatedAt])]);
  const [, refresh] = useState(0);
  useEffect(() => {
    let active = true, timer;
    const load = async () => {
      // Sequential requests keep the lightweight model workload bounded.
      for (const candidate of candidates) {
        if (!active) return;
        const entryKey = cacheKey(identity, mode, candidate);
        if (cached(entryKey)) continue;
        try { await fetchSuggestion(entryKey, candidate, mode); } catch { /* No title-based fallback. */ }
        if (active) refresh(value => value + 1);
      }
      if (active) timer = setTimeout(load, TTL);
    };
    if (enabled && userId) load();
    return () => { active = false; clearTimeout(timer); };
  }, [key]); // Key captures account, session, mode, eligibility and every conversation revision.
  if (!enabled || !userId) return [];
  // Select from today's recent-chat list on every render, never from cached membership.
  return candidates.flatMap(candidate => {
    const suggestion = cached(cacheKey(identity, mode, candidate))?.suggestion;
    return suggestion ? [{ ...candidate, title: suggestion.prompt, prompt: suggestion.prompt }] : [];
  });
}
